import { IMG, FLAGS } from "./shared";

const en = {
  langName: "EN",
  nav: {
    brand: "Barlow Consultants",
    brandSub: "SL · LUXEMBOURG",
    links: [
      { label: "Fixed Deposit", href: "#konditionen" },
      { label: "Security", href: "#sicherheit" },
      { label: "Wealth Management", href: "#vermoegen" },
      { label: "Advisory", href: "#beratung" },
      { label: "Team", href: "#team" },
      { label: "Interest Calculator", href: "#rechner" },
    ],
  },
  ui: {
    login: "Sign in",
    openAccount: "Open account",
    portal: "Client portal",
    invest: "Invest",
    ratePa: "Interest p.a.",
  },
  hero: {
    eyebrow: "Private & Corporate Fixed Deposits · Luxembourg",
    titlePre: "Secure fixed deposits with",
    titleAccent: "predictable",
    titlePost: "returns.",
    text: "Open your fixed-deposit account with Barlow Consultants SL – for private individuals and companies (corporate fixed deposits). Let your capital work for you at attractive fixed rates – transparent, secure and fully digital.",
    network: "Access to more than 50 European banks – plus leading banks in the United Kingdom.",
    badge1: "incl. personal wealth advisory",
    badge2: "Doubly secured · Deposit guarantee + trust account",
    btnOpen: "Open account now",
    btnConditions: "View conditions",
    btnAdvice: "Discover advisory",
    floatGuarantee: "Your deposits are legally protected up to €100,000 per customer and bank.",
    floatRateLabel: "Fixed interest p.a.",
    image: IMG.hero,
    stats: [
      { icon: "Landmark", label: "Held at a regulated partner bank" },
      { icon: "TrendingUp", label: "Fixed interest up to 4.25%" },
      { icon: "CalendarClock", label: "Terms from 6 to 60 months" },
    ],
  },
  network: {
    eyebrow: "Our banking network",
    title: "A strong network of European banks",
    text: "We work with more than 50 banks across Europe and additionally provide access to selected leading banks in the United Kingdom.",
    overlay1: "European banks",
    overlay2: "+ leading UK banks",
    btnDiscover: "Discover deposit offers",
    btnCompare: "Compare offers",
    image: IMG.network,
    cards: [
      { icon: "Globe", title: "50+ European banks", text: "Access to an extensive network of banks from various European countries." },
      { icon: "Building2", title: "Additional UK banks", text: "We additionally cooperate with selected leading banks from the United Kingdom." },
      { icon: "Layers", title: "Many offers – one overview", text: "Our platform lets prospective clients compare various fixed-deposit offers at a glance." },
    ],
    flagsLabel: "Represented across Europe and in the United Kingdom",
    flags: FLAGS,
  },
  conditions: {
    title: "Fixed-Deposit Conditions",
    text: "Fixed interest rates for the entire term. No market risk, full predictability.",
    invest: "Invest",
    ratePa: "Interest p.a.",
    rows: [
      { term: "6 Months", months: 6, rate: 2.8 },
      { term: "12 Months", months: 12, rate: 3.3 },
      { term: "24 Months", months: 24, rate: 3.6 },
      { term: "36 Months", months: 36, rate: 3.9 },
      { term: "60 Months", months: 60, rate: 4.25 },
    ],
  },
  security: {
    eyebrow: "Double Security",
    title: "Your capital – protected twice",
    text: "Your deposits are protected twice: by the statutory European deposit guarantee and, in addition, by being held in a trust account.",
    layers: [
      {
        level: "Layer 1", badge: "up to €100,000", badgeSub: "protected per depositor",
        title: "Statutory deposit guarantee",
        text: "Funds held at a regulated partner bank in Luxembourg are subject to Luxembourg's statutory deposit guarantee scheme (FGDL – Fonds de garantie des dépôts Luxembourg). In line with EU Directive 2014/49/EU, deposits are protected up to €100,000 per depositor and institution.",
        note: "Held at a regulated partner bank in Luxembourg · Protected in accordance with EU Directive 2014/49/EU",
        link: "More about deposit protection",
      },
      {
        level: "Layer 2", badge: "Trust account", badgeSub: "additional protection",
        title: "Additional trust protection",
        text: "In addition to the statutory deposit guarantee, your deposits are managed in trust on a trust account according to clear custody rules.",
        bullets: [
          "Fiduciary management according to clear custody rules",
          "An additional layer of protection beyond the deposit guarantee",
        ],
      },
    ],
  },
  whyUs: {
    title: "Why Barlow Consultants",
    items: [
      { icon: "ShieldCheck", title: "Capital security", text: "Your deposits are held at a regulated partner bank in Luxembourg." },
      { icon: "Percent", title: "Fixed return", text: "The interest rate is fixed upon signing – for the entire term." },
      { icon: "Eye", title: "Full transparency", text: "Insight into balance, investments and interest forecasts in your portfolio at any time." },
      { icon: "MonitorSmartphone", title: "Digital onboarding", text: "Open your account online in just minutes – entirely without paperwork." },
    ],
  },
  wealth: {
    eyebrow: "Private Wealth Management · Luxembourg",
    title: "More than fixed deposits – your private wealth management.",
    text: "Barlow Consultants SL is not only your partner for fixed deposits, but also for the professional management of your wealth. We develop a strategy tailored to you and accompany you long-term with discretion and foresight.",
    image: IMG.wealth,
    cta: "Request advisory",
    items: [
      { icon: "Target", title: "Individual investment strategy", text: "A portfolio aligned with your goals, your time horizon and your risk appetite." },
      { icon: "Activity", title: "Active portfolio management", text: "Broadly diversified investments across asset classes – continuously monitored and adjusted." },
      { icon: "UserRound", title: "Personal support", text: "A dedicated contact who knows your financial situation and is available to you." },
      { icon: "Shield", title: "Wealth protection", text: "Capital preservation and protection are central – for you and the next generation." },
    ],
  },
  advisory: {
    eyebrow: "Personal Wealth Advisory",
    title: "A personal advisor at your side",
    text: "More than fixed deposits: our experienced advisors develop an investment strategy together with you that fits your goals, your life situation and your need for security – transparent, independent and without sales pressure.",
    image: IMG.advisory,
    suitableTitle: "Who it is for",
    cta: "Arrange a consultation",
    suitable: [
      "Savers who want to invest their wealth long-term and securely",
      "People approaching or in retirement seeking predictable income",
      "Investors who value personal support over anonymous apps",
    ],
    items: [
      { icon: "Target", title: "Individual strategy", text: "Your investment is tailored to your goals, terms and risk appetite." },
      { icon: "UserRound", title: "Dedicated contact", text: "A personal advisor accompanies you – accessible, clear and reliable." },
      { icon: "Receipt", title: "Transparent costs", text: "Clear conditions without hidden fees. You always know where you stand." },
      { icon: "RefreshCw", title: "Ongoing support", text: "We review your strategy regularly and adjust it when needed." },
    ],
  },
  process: {
    eyebrow: "How your advisory works",
    title: "Four steps to your strategy",
    text: "Straightforward, non-binding and at your own pace.",
    steps: [
      { no: "01", title: "Initial talk", text: "We get to know you and your goals – free of charge and non-binding." },
      { no: "02", title: "Analysis", text: "We analyse your situation and your investment horizon in detail." },
      { no: "03", title: "Strategy", text: "You receive a clear, understandable investment proposal." },
      { no: "04", title: "Support", text: "We implement the strategy and support you continuously." },
    ],
    stats: [
      { value: "500+", label: "Clients supported" },
      { value: "15 years", label: "Experience in wealth advisory" },
      { value: "100%", label: "Personal contact" },
      { value: "€0", label: "Cost for the initial talk" },
    ],
  },
  corporate: {
    eyebrow: "Corporate Advisory",
    title: "Your personal corporate advisors",
    text: "Dedicated contacts for corporate fixed deposits – competent, discreet and always available to you.",
    ctaTitle: "Ready for a personal conversation?",
    ctaText: "Arrange your free and non-binding consultation.",
    ctaBtn: "Arrange a consultation",
    people: [
      { initials: "LP", name: "Leon Peterson", role: "Corporate advisor", region: "DACH region Germany", phone: "+49 30 8268 8142" },
      { initials: "TB", name: "Tanja Berg", role: "Corporate advisor", region: "DACH region Germany", phone: "+49 30 8268 5344" },
      { initials: "DW", name: "Dr. Denis Weilandt", role: "Corporate advisor", region: "DACH region Germany", phone: "+49 30 8268 6108" },
    ],
  },
  strategies: {
    eyebrow: "Investment Strategies",
    title: "Three strategies for your wealth.",
    text: "From capital-preserving to growth-oriented – we find the right strategy for your goals. The returns shown are target values and not a guarantee.",
    expectedReturn: "Expected return p.a.",
    risk: "Risk",
    allocation: "Portfolio allocation",
    popular: "Popular",
    cta: "Discuss strategy",
    items: [
      { name: "Conservative", ret: "2 – 4%", risk: "Low", popular: false, text: "Focus on capital preservation: fixed deposits, high-grade bonds and liquidity. Low volatility.", alloc: [{ k: "Fixed deposit", v: 70 }, { k: "Bonds", v: 25 }, { k: "Equities", v: 5 }] },
      { name: "Balanced", ret: "4 – 6%", risk: "Medium", popular: true, text: "A balanced mix of bonds and equities for steady growth at moderate risk.", alloc: [{ k: "Fixed deposit", v: 30 }, { k: "Bonds", v: 35 }, { k: "Equities", v: 35 }] },
      { name: "Growth", ret: "6 – 9%", risk: "High", popular: false, text: "Equity-focused orientation for long-term wealth building – higher volatility possible.", alloc: [{ k: "Fixed deposit", v: 10 }, { k: "Bonds", v: 25 }, { k: "Equities", v: 65 }] },
    ],
  },
  steps: {
    title: "Three steps to your fixed deposit",
    stepWord: "Step",
    items: [
      { no: "01", title: "Open account", text: "Register free of charge and receive your personal account with a personal reference number." },
      { no: "02", title: "Deposit capital", text: "Transfer your investment amount to the designated custody account." },
      { no: "03", title: "Invest in fixed deposit", text: "Choose term and amount – your return runs automatically." },
    ],
  },
  fees: {
    eyebrow: "Transparency",
    title: "Fee overview",
    note: "No hidden costs – full transparency.",
    rows: [
      { k: "Account opening", v: "€0" },
      { k: "Deposit", v: "€0" },
      { k: "Administration / account management", v: "€0" },
      { k: "Performance fee", v: "none (only for wealth management)" },
      { k: "Payout at maturity", v: "€0" },
      { k: "Other fees", v: "no hidden fees" },
    ],
  },
  calculator: {
    title: "Interest Calculator",
    subtitle: "Calculate your return and see how your capital grows over the years with automatic reinvestment.",
    amount: "Investment amount",
    term: "Term",
    horizon: "Time horizon",
    years: "years",
    ratePa: "Interest p.a.",
    interestTerm: "Interest (term)",
    payout: "Payout",
    capitalAfter: "Capital after",
    developmentReinvest: "Development with automatic reinvestment",
    totalInterest: "Total interest",
    openAccount: "Open account",
  },
  team: {
    eyebrow: "Our Team",
    title: "The responsible team",
    text: "Clear responsibilities and dedicated contacts – for your trust.",
    members: [
      { name: "Francesco Vigna", role: "Management", img: IMG.team[0], text: "Francesco Vigna is responsible for the strategic direction of Barlow Consultants SL and brings many years of experience in fixed-deposit and wealth-management products." },
      { name: "Isabel Moreno", role: "Head of Client Care", img: IMG.team[1], text: "Isabel Moreno is the first point of contact for our clients and ensures a personal, transparent service throughout every phase of the investment." },
      { name: "Henrik Larsson", role: "Head of Investment Strategy", img: IMG.team[2], text: "Henrik Larsson develops and monitors our investment strategies and ensures a balanced ratio of opportunity and risk." },
    ],
  },
  testimonials: {
    eyebrow: "Client Voices",
    title: "Verified client voices",
    text: "Real experiences from our clients – transparently marked.",
    customerSince: "Client since",
    items: [
      { quote: "The entire process was transparent and straightforward – from opening the account to the punctual interest credit at maturity. That's exactly how one wishes fixed deposits to be.", name: "Markus Hoffmann", country: "Germany", since: "2025 · verified" },
      { quote: "What convinced me: fees and risks are clearly stated, without any fine print. The personal contact was always competent and friendly.", name: "Sophie Berger", country: "Luxembourg", since: "2025 · verified" },
      { quote: "I compared several providers. The clear cost structure and the understandable overview in the client account were decisive.", name: "Daniel Rößler", country: "Austria", since: "2024 · verified" },
      { quote: "Serious, understandable and reliable. My questions were always answered quickly and the conditions were implemented exactly as discussed.", name: "Elena Fischer", country: "Switzerland", since: "2025 · verified" },
    ],
  },
  contact: {
    eyebrow: "Consultation",
    title: "Arrange a free consultation",
    text: "Leave us your details – a personal advisor will get in touch with you shortly. Non-binding and free of charge.",
    perks: ["Individual strategy", "Dedicated contact", "Transparent costs", "Ongoing support"],
    form: {
      name: "Full name",
      email: "Email address",
      phone: "Phone (optional)",
      amount: "Planned investment amount (optional)",
      message: "Your message (optional)",
      submit: "Request advisory",
    },
    toastErr: "Please provide your name and email address.",
    toastOk: "Thank you! An advisor will get in touch with you shortly.",
  },
  finalCta: {
    title: "Ready to put your capital to work?",
    text: "Open your fixed-deposit account with Barlow Consultants SL today.",
    button: "Open account now",
  },
  cookie: {
    text: "We use necessary cookies for the operation of the site as well as optional cookies to improve your experience. You decide what to allow.",
    necessary: "Necessary only",
    all: "Accept all",
    more: "Learn more",
  },
  footer: {
    tagline: "Fixed deposits & wealth advisory with European deposit protection and trust account.",
    navTitle: "Navigation",
    legalTitle: "Legal",
    legalLinks: ["Imprint", "Privacy", "Terms", "Deposit protection", "Cookies"],
    contactTitle: "Contact",
    contactLines: ["Barlow Consultants SL", "Luxembourg"],
    deOfficeTitle: "Germany office",
    deOfficeLines: ["Barlow Consultants", "Kurfürstendamm 30", "10719 Berlin", "Germany"],
    rights: "All rights reserved.",
    disclaimer: "Deposits protected up to €100,000 in accordance with EU Directive 2014/49/EU.",
  },
  accountModal: {
    title: "Open a fixed-deposit account",
    desc: "Free of charge and in just minutes – entirely without paperwork.",
    name: "Full name",
    email: "Email address",
    amount: "Amount (€)",
    submit: "Open account",
    note: "protected up to €100,000 by the FGDL",
    toastErr: "Please provide your name and email address.",
    toastOk: "Account request received!",
    errGeneric: "An error occurred. Please try again.",
    successTitle: "Account request received!",
    successText: "Thank you. Your request has been recorded.",
    refLabel: "Your personal reference number",
    successNote: "Please note this reference number. An advisor will contact you shortly.",
    done: "Done",
    copied: "Reference number copied",
  },
  portal: {
    backHome: "Back to website",
    logout: "Sign out",
    loginTitle: "Welcome to the client portal",
    loginSubtitle: "Sign in to view your balance, investments and interest forecasts.",
    loginHeadline: "Welcome back",
    loginSub: "Sign in to your account.",
    authImageTitle: "Secure fixed deposits with predictable returns.",
    authImageSub: "Held at Banking Circle S.A. · Fixed rates up to 4.25%",
    emailLabel: "Email address",
    passwordLabel: "Password",
    loginBtn: "Sign in",
    demoTitle: "Demo access",
    demoHint: "To try it out, please use:",
    useDemo: "Fill in demo data",
    loginErr: "Invalid credentials. Please use the demo access.",
    loginOk: "Signed in successfully.",
    welcome: "Welcome,",
    refNo: "Reference number",
    totalBalance: "Total capital",
    investedCapital: "Invested capital",
    projectedInterest: "Expected interest until maturity",
    avgRate: "Avg. interest rate",
    activeDeposits: "Active fixed-deposit investments",
    colAmount: "Investment amount",
    colTerm: "Term",
    colRate: "Rate",
    colMaturity: "Maturity",
    colInterest: "Interest earned",
    colStatus: "Status",
    statusActive: "Active",
    months: "months",
    forecastTitle: "Interest forecast",
    forecastNote: "Projected capital development with automatic reinvestment",
    years: "years",
    afterYears: "Capital after",
    newDeposit: "Open new investment",
    registerTitle: "Open account",
    registerSubtitle: "Create your personal fixed-deposit account.",
    depositNote: "up to €100,000 protected",
    accountTypeLabel: "Account type",
    accTypePrivate: "Individual",
    accTypeCompany: "Company",
    nameLabel: "Full name",
    countryLabel: "Country",
    countryPlaceholder: "Select country",
    phoneLabel: "Phone",
    consentIntro: "I accept the",
    consentTerms: "Terms of Use",
    consentAgb: "Terms & Conditions",
    consentMid: "and the",
    consentPrivacy: "Privacy Policy",
    errConsent: "Please accept the Terms & Conditions and the Privacy Policy.",
    registerBtn: "Open account",
    noAccount: "No account yet?",
    toRegister: "Register now",
    haveAccount: "Already registered?",
    toLogin: "Sign in",
    registerOk: "Account created successfully.",
    errEmailExists: "This email address is already registered.",
    errPassword: "The password must be at least 6 characters long.",
    errEmailInvalid: "Please provide a valid email address.",
    errGeneric: "An error occurred. Please try again.",
    changePwTitle: "Change password",
    changePwSubtitle: "Update your password regularly to keep your account secure.",
    currentPwLabel: "Current password",
    newPwLabel: "New password",
    confirmPwLabel: "Confirm new password",
    changePwBtn: "Change password",
    changePwOk: "Password changed successfully.",
    errWrongPw: "The current password is incorrect.",
    errPwMismatch: "The new passwords do not match.",
    errSamePw: "The new password must differ from the current one.",
    forgotPw: "Forgot password?",
    forgotTitle: "Reset password",
    forgotSubtitle: "Enter your email address and we'll send you a reset link.",
    forgotBtn: "Send link",
    forgotOk: "If an account exists, we've sent you a reset link.",
    backToLogin: "Back to sign in",
    resetTitle: "Set a new password",
    resetSubtitle: "Choose a new password for your account.",
    resetBtn: "Save password",
    resetOk: "Password reset successfully. You can now sign in.",
    errInvalidToken: "This link is invalid or has already been used.",
    errExpiredToken: "This link has expired. Please request a new one.",
    resetNoToken: "No valid reset link. Please request a new one.",
    emptyDeposits: "You have no fixed-deposit investments yet. Open your first one now.",
    loading: "Loading …",
    sbOverview: "Overview",
    sbFestgeld: "Fixed Deposit",
    sbTransactions: "Transactions",
    sbDocuments: "Documents",
    sbForecast: "Forecast",
    sbStrategy: "Investment strategy",
    sbSecurity: "Security",
    availableBalance: "Available balance",
    accountNumber: "Account number",
    statusLabel: "Status",
    deposit: "Deposit money",
    requestWithdrawal: "Request payout",
    statementPdf: "Statement (PDF)",
    securityBanner: "protected up to €100,000 per depositor. Held at a regulated partner bank in Luxembourg · Protected under EU Directive 2014/49/EU.",
    securityBannerBold: "protected up to €100,000 per depositor.",
    securityBannerText: "Access to over 50 European banks – plus leading UK banks · Protected under EU Directive 2014/49/EU",
    securityBannerLink: "More on deposit protection",
    securityBannerExtra: "In addition, your deposits are held in trust on a trust account.",
    depositTitle: "Deposit money",
    withdrawTitle: "Request payout",
    investTitle: "Invest in fixed deposit",
    amountLabel: "Amount (€)",
    termLabel: "Term",
    confirm: "Confirm",
    cancel: "Cancel",
    depositOk: "Deposit credited successfully.",
    withdrawOk: "Payout requested.",
    investOk: "Fixed deposit created successfully.",
    insufficient: "Insufficient available balance.",
    invalidAmount: "Please enter a valid amount.",
    noTransactions: "No transactions yet.",
    txDeposit: "Deposit",
    txWithdrawal: "Payout",
    txInvest: "Fixed-deposit investment",
    colDate: "Date",
    colType: "Type",
    statusCompleted: "Completed",
    statusPending: "Pending",
    documentsTitle: "Documents",
    documentsDesc: "Download your current account statement as a PDF.",
    invest: "Invest",
    statementHeading: "Account statement",
    generatedOn: "Generated on",
  },
  chat: {
    title: "Barlow Assistant",
    subtitle: "Questions on fixed deposits, security & fees",
    greeting: "Hello! I'm happy to answer your questions about our fixed-deposit offers, deposit protection and fees. How can I help?",
    faqLabel: "Frequent questions",
    faqs: ["How safe is my money?", "What interest do you offer?", "What fees apply?", "How do I open an account?"],
    placeholder: "Write a message …",
    error: "Sorry, something went wrong. Please try again later.",
    open: "Open chat",
    close: "Close",
  },
};

export default en;
